import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2, FiEye } from 'react-icons/fi';
import '../Students/StudentsPage.css';

const TextsPage = () => {
    const [texts, setTexts] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [editingText, setEditingText] = useState(null);
    const [viewingText, setViewingText] = useState(null);
    const [viewingSentences, setViewingSentences] = useState([]);
    const [selectedLesson, setSelectedLesson] = useState('');

    const [formData, setFormData] = useState({
        lessonId: '',
        title: '',
        description: '',
        fullTextEn: '',
        fullTextRu: '',
        sortOrder: 0
    });

    useEffect(() => {
        loadData();
    }, [selectedLesson]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [levelsResponse, lessonsResponse, textsResponse] = await Promise.all([
                adminAPI.getLevels(),
                adminAPI.getLessons(),
                adminAPI.getTexts(selectedLesson || undefined)
            ]);
            setLevels(levelsResponse.data.levels);
            setLessons(lessonsResponse.data.lessons);
            setTexts(textsResponse.data.texts);
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке данных');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingText) {
                await adminAPI.updateText(editingText.id, formData);
            } else {
                await adminAPI.createText(formData);
            }
            
            setShowModal(false);
            setEditingText(null);
            resetForm();
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении текста');
        }
    };

    const handleView = async (text) => {
        try {
            const response = await adminAPI.getText(text.id);
            setViewingText(response.data.text);
            setViewingSentences(response.data.sentences);
            setShowViewModal(true);
        } catch (err) {
            setError('Ошибка при загрузке предложений');
        }
    };

    const handleEdit = (text) => {
        setEditingText(text);
        setFormData({
            lessonId: text.lesson_id,
            title: text.title,
            description: text.description || '',
            fullTextEn: text.full_text_en,
            fullTextRu: text.full_text_ru || '',
            sortOrder: text.sort_order || 0
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот текст? Все предложения и упражнения также будут удалены!')) {
            return;
        }

        try {
            await adminAPI.deleteText(id);
            loadData();
        } catch (err) {
            setError('Ошибка при удалении текста');
        }
    };

    const resetForm = () => {
        setFormData({
            lessonId: '',
            title: '',
            description: '',
            fullTextEn: '',
            fullTextRu: '',
            sortOrder: 0
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingText(null);
        resetForm();
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingText(null);
        setViewingSentences([]);
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Тексты</h1>
                    <p>Управление текстами с автоматической разбивкой на предложения</p>
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowModal(true)}
                >
                    <FiPlus /> Добавить текст
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card" style={{ marginBottom: '20px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Фильтр по уроку:</label>
                    <select 
                        value={selectedLesson} 
                        onChange={(e) => setSelectedLesson(e.target.value)}
                        style={{ width: '400px' }}
                    >
                        <option value="">Все уроки</option>
                        {lessons.map(lesson => (
                            <option key={lesson.id} value={lesson.id}>
                                {lesson.level_name} - {lesson.title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Урок/Уровень</th>
                                <th>Название текста</th>
                                <th>Описание</th>
                                <th>Предложений</th>
                                <th>Статус</th>
                                <th>Дата создания</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {texts.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        Нет текстов. Создайте первый текст.
                                    </td>
                                </tr>
                            ) : (
                                texts.map((text) => (
                                    <tr key={text.id}>
                                        <td>
                                            <div style={{ fontSize: '12px', color: '#999' }}>
                                                {text.level_name}
                                            </div>
                                            <div>{text.lesson_title}</div>
                                        </td>
                                        <td><strong>{text.title}</strong></td>
                                        <td>{text.description || '—'}</td>
                                        <td>
                                            <span style={{ 
                                                background: '#f0f0f0', 
                                                padding: '4px 8px', 
                                                borderRadius: '4px',
                                                fontSize: '12px'
                                            }}>
                                                {text.sentences_count} шт.
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${text.is_active ? 'active' : 'inactive'}`}>
                                                {text.is_active ? 'Активен' : 'Неактивен'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(text.created_at).toLocaleDateString('ru-RU')}
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleView(text)}
                                                    title="Просмотр предложений"
                                                >
                                                    <FiEye />
                                                </button>
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(text)}
                                                    title="Редактировать"
                                                >
                                                    <FiEdit />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(text.id)}
                                                    title="Удалить"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingText ? 'Редактировать текст' : 'Добавить текст'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Урок *</label>
                                <select
                                    name="lessonId"
                                    value={formData.lessonId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Выберите урок</option>
                                    {lessons.map(lesson => (
                                        <option key={lesson.id} value={lesson.id}>
                                            {lesson.level_name} - {lesson.title}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Название текста *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Например: Важное решение"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Описание</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Краткое описание текста"
                                    rows="2"
                                />
                            </div>

                            <div className="form-group">
                                <label>Текст на английском *</label>
                                <textarea
                                    name="fullTextEn"
                                    value={formData.fullTextEn}
                                    onChange={handleInputChange}
                                    placeholder="Введите текст на английском языке. Он будет автоматически разбит на предложения."
                                    rows="8"
                                    required
                                />
                                <small style={{ color: '#999', fontSize: '12px' }}>
                                    Текст будет автоматически разбит на предложения по точкам, восклицательным и вопросительным знакам.
                                </small>
                            </div>

                            <div className="form-group">
                                <label>Текст на русском (опционально)</label>
                                <textarea
                                    name="fullTextRu"
                                    value={formData.fullTextRu}
                                    onChange={handleInputChange}
                                    placeholder="Введите перевод на русский язык"
                                    rows="8"
                                />
                                <small style={{ color: '#999', fontSize: '12px' }}>
                                    Количество предложений должно совпадать с английским текстом для правильного сопоставления.
                                </small>
                            </div>

                            <div className="form-group">
                                <label>Порядок сортировки</label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingText ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showViewModal && viewingText && (
                <div className="modal-overlay" onClick={handleCloseViewModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>{viewingText.title}</h2>
                                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
                                    {viewingText.lesson_title} | {viewingText.level_name}
                                </p>
                            </div>
                            <button onClick={handleCloseViewModal} className="modal-close">×</button>
                        </div>

                        <div className="modal-form">
                            <div style={{ marginBottom: '20px' }}>
                                <strong>Всего предложений:</strong> {viewingSentences.length}
                            </div>

                            <div className="sentences-list">
                                {viewingSentences.map((sentence, index) => (
                                    <div key={sentence.id} className="sentence-item">
                                        <div className="sentence-number">{index + 1}</div>
                                        <div className="sentence-content">
                                            <div className="sentence-en">{sentence.sentence_en}</div>
                                            {sentence.sentence_ru && (
                                                <div className="sentence-ru">{sentence.sentence_ru}</div>
                                            )}
                                            {sentence.audio_url && (
                                                <div className="sentence-audio">
                                                    <span>🔊 Аудио: {sentence.audio_url}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .modal-large {
                    max-width: 800px;
                }
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.active {
                    background-color: #f6ffed;
                    color: #52c41a;
                    border: 1px solid #b7eb8f;
                }
                .status-badge.inactive {
                    background-color: #fff2f0;
                    color: #f5222d;
                    border: 1px solid #ffccc7;
                }
                .sentences-list {
                    max-height: 500px;
                    overflow-y: auto;
                }
                .sentence-item {
                    display: flex;
                    gap: 15px;
                    padding: 15px;
                    border: 1px solid #e8e8e8;
                    border-radius: 8px;
                    margin-bottom: 12px;
                    background: #fafafa;
                }
                .sentence-number {
                    flex-shrink: 0;
                    width: 32px;
                    height: 32px;
                    background: #4a90e2;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 14px;
                }
                .sentence-content {
                    flex: 1;
                }
                .sentence-en {
                    font-size: 15px;
                    line-height: 1.6;
                    margin-bottom: 8px;
                    color: #2c3e50;
                }
                .sentence-ru {
                    font-size: 14px;
                    line-height: 1.5;
                    color: #7f8c8d;
                    font-style: italic;
                }
                .sentence-audio {
                    margin-top: 8px;
                    font-size: 12px;
                    color: #4a90e2;
                }
            `}</style>
        </div>
    );
};

export default TextsPage;
